/**
 * Zoom Meeting SDK (Client View) CDN loader.
 *
 * The Client View bundle at v3.13.0 expects its React/Redux/Lodash peers as
 * `window` globals (e.g. throws `ReferenceError: React is not defined` on
 * first parse otherwise). Zoom ships vendored copies at
 * `https://source.zoom.us/3.13.0/lib/vendor/` — we load those first in
 * strict order, then the SDK. Same approach as the now-deleted Component
 * View loader.
 */

const ZOOM_VERSION = '3.13.0'
const CSS_BASE = `https://source.zoom.us/${ZOOM_VERSION}/css`
const VENDOR_BASE = `https://source.zoom.us/${ZOOM_VERSION}/lib/vendor`
const SDK_SRC = `https://source.zoom.us/${ZOOM_VERSION}/zoom-meeting-${ZOOM_VERSION}.min.js`

const VENDOR_SCRIPTS = [
  `${VENDOR_BASE}/react.min.js`,
  `${VENDOR_BASE}/react-dom.min.js`,
  `${VENDOR_BASE}/redux.min.js`,
  `${VENDOR_BASE}/redux-thunk.min.js`,
  `${VENDOR_BASE}/lodash.min.js`,
]

let loadPromise = null

const injectStyle = href => {
  if (document.querySelector(`link[href="${href}"]`)) return
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.type = 'text/css'
  link.href = href
  document.head.appendChild(link)
}

const injectScript = src =>
  new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`)
    if (existing) {
      if (existing.dataset.loaded === '1') return resolve()
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)))
      return
    }
    const script = document.createElement('script')
    script.src = src
    script.async = false
    script.defer = false
    script.onload = () => {
      script.dataset.loaded = '1'
      resolve()
    }
    script.onerror = () => reject(new Error(`Failed to load ${src}`))
    document.head.appendChild(script)
  })

export const loadClientViewSdk = () => {
  if (loadPromise) return loadPromise
  loadPromise = (async () => {
    injectStyle(`${CSS_BASE}/bootstrap.css`)
    injectStyle(`${CSS_BASE}/react-select.css`)
    // Strictly sequential: each vendor script binds globals (window.React,
    // window.ReactDOM, etc.) that the next file references at parse time.
    for (const src of VENDOR_SCRIPTS) {
      // eslint-disable-next-line no-await-in-loop
      await injectScript(src)
    }
    await injectScript(SDK_SRC)
    if (!window.ZoomMtg) {
      throw new Error('ZoomMtg global was not exposed after script load')
    }
    return window.ZoomMtg
  })()
  return loadPromise
}

export const isClientViewSdkLoaded = () => !!window.ZoomMtg

export { ZOOM_VERSION }
